import { useState, useEffect, useCallback } from 'react'
import 'react-calendar/dist/Calendar.css'
import Calendar from 'react-calendar'
import { CalendarBlankIcon } from '@phosphor-icons/react'
import { ScoopButton, ScoopText, ScoopMenu } from '..'
import styles from './ScoopDatePicker.module.scss'

const shortDays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

const rangeOptions = [
    'Last 7 Days',
    'Last 14 Days',
    'Last Month',
    'Last 2 Months',
    'Last 6 Months',
    'Last Year',
]

/**
 * Helper to normalize date to string for comparison
 */
const normalizeDateToString = (date) => {
    if (!date) return null
    const d = new Date(date)
    return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`
}

/**
 * ScoopDatePicker - Unified date picker component
 *
 * @param {Date|Date[]} value - Selected date or date range
 * @param {Function} onChange - Callback when date changes
 * @param {boolean} range - Enable range selection mode
 * @param {string} className - Additional CSS class for container
 * @param {Object} style - Inline styles for container
 * @param {string} label - Label text
 * @param {boolean} isGuestMode - Disable picker in guest mode
 * @param {string[]} enabledDates - Array of date strings (MM/DD/YYYY) that are selectable. All other dates will be disabled.
 * @param {Array<{date: string, content: string}>} markedDates - Array of dates with content to display (e.g., emoji markers)
 * @param {boolean} instant - If true, selection is immediate without Apply/Cancel buttons
 */
export const ScoopDatePicker = ({
    value,
    onChange,
    range,
    className,
    style,
    label,
    isGuestMode,
    enabledDates,
    markedDates,
    instant = false,
}) => {
    const [innerValue, setInnerValue] = useState(value)
    const [showPicker, setShowPicker] = useState(false)
    const [memoRangeOption, setMemoRangeOption] = useState('')
    const [rangeOption, setRangeOption] = useState('')
    const [anchorEl, setAnchorEl] = useState(null)

    useEffect(() => {
        setInnerValue(null)
    }, [range])

    useEffect(() => {
        if (showPicker) {
            setInnerValue(value)
            setMemoRangeOption(rangeOption)
        }
    }, [showPicker])

    const handleDateChange = (newValue) => {
        if (instant) {
            onChange(newValue)
            setShowPicker(false)
        } else {
            setInnerValue(newValue)
            setRangeOption('')
        }
    }

    // Disable dates not in enabledDates array
    const tileDisabled = useCallback(
        ({ date, view }) => {
            if (!enabledDates || view !== 'month') return false
            const dateStr = normalizeDateToString(date)
            return !enabledDates.includes(dateStr)
        },
        [enabledDates]
    )

    // Render content (markers) on specific dates
    const tileContent = useCallback(
        ({ date, view }) => {
            if (!markedDates || view !== 'month') return null
            const dateStr = normalizeDateToString(date)
            const marked = markedDates.find((m) => m.date === dateStr)
            if (marked) {
                return <span className={styles.tileMarker}>{marked.content}</span>
            }
            return null
        },
        [markedDates]
    )

    const handleRangeOptionSelect = (option) => {
        setRangeOption(option)
        const today = new Date()
        const startDate = new Date()
        const firstDayOfCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1)
        const lastDayOfLastMonth = new Date(firstDayOfCurrentMonth)
        lastDayOfLastMonth.setDate(lastDayOfLastMonth.getDate() - 1)
        switch (option) {
            case rangeOptions[0]:
                startDate.setDate(today.getDate() - 7)
                setInnerValue([startDate, today])
                break
            case rangeOptions[1]:
                startDate.setDate(today.getDate() - 14)
                setInnerValue([startDate, today])
                break
            case rangeOptions[2]:
                const firstDayOfLastMonth = new Date(firstDayOfCurrentMonth)
                firstDayOfLastMonth.setMonth(firstDayOfLastMonth.getMonth() - 1)
                setInnerValue([firstDayOfLastMonth, lastDayOfLastMonth])
                break
            case rangeOptions[3]:
                const firstDayOfLastTwoMonths = new Date(firstDayOfCurrentMonth)
                firstDayOfLastTwoMonths.setMonth(firstDayOfLastTwoMonths.getMonth() - 2)
                setInnerValue([firstDayOfLastTwoMonths, lastDayOfLastMonth])
                break
            case rangeOptions[4]:
                const firstDayOfLastSixMonths = new Date(firstDayOfCurrentMonth)
                firstDayOfLastSixMonths.setMonth(firstDayOfLastSixMonths.getMonth() - 6)
                setInnerValue([firstDayOfLastSixMonths, lastDayOfLastMonth])
                break
            case rangeOptions[5]:
                const firstDayOfLastYear = new Date(firstDayOfCurrentMonth)
                firstDayOfLastYear.setMonth(firstDayOfLastYear.getMonth() - 12)
                setInnerValue([firstDayOfLastYear, lastDayOfLastMonth])
                break
            default:
                break
        }
    }

    const handleCancel = () => {
        setShowPicker(false)
        if (memoRangeOption !== rangeOption) setRangeOption(memoRangeOption)
    }

    const handleApply = () => {
        onChange(innerValue)
        setShowPicker(false)
    }

    const getStringValue = () => {
        if (!value) {
            if (range) {
                return 'Select date range'
            } else {
                return 'Select date'
            }
        } else {
            if (range) {
                return Array.isArray(value)
                    ? value[0].toLocaleDateString('en-US') +
                          ' - ' +
                          value[1].toLocaleDateString('en-US')
                    : ''
            } else {
                return Array.isArray(value) ? '' : value.toLocaleDateString('en-US')
            }
        }
    }

    const getInnerValueDateString = () => {
        if (!innerValue) {
            return ''
        } else {
            if (range) {
                return Array.isArray(innerValue)
                    ? innerValue[0].toDateString() + ' - ' + innerValue[1].toDateString()
                    : ''
            } else {
                return Array.isArray(innerValue) ? '' : innerValue.toDateString()
            }
        }
    }

    const containerClassName = [styles.datePickerContainer, className].filter(Boolean).join(' ')

    return (
        <>
            <div className={containerClassName} style={style}>
                {label && <ScoopText className={styles.label}>{label}</ScoopText>}
                <div
                    className={styles.datePickerInput}
                    ref={setAnchorEl}
                    onClick={() => !isGuestMode && setShowPicker(true)}
                >
                    <ScoopText className={styles.inputText}>{getStringValue()}</ScoopText>
                    <CalendarBlankIcon size={18} />
                </div>
            </div>
            <ScoopMenu
                open={showPicker}
                anchorEl={anchorEl}
                onClose={handleCancel}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                className={styles.datePickerPopup}
            >
                <div className={styles.optionsAndCalendar}>
                    {range && !instant && (
                        <div className={styles.rangeOptionsContainer}>
                            {rangeOptions.map((option) => (
                                <div
                                    key={option}
                                    className={`${styles.rangeOption} ${option === rangeOption ? styles.rangeOptionSelected : ''}`}
                                    onClick={() => handleRangeOptionSelect(option)}
                                >
                                    <ScoopText className={styles.rangeOptionText}>
                                        {option}
                                    </ScoopText>
                                </div>
                            ))}
                        </div>
                    )}
                    <div className={styles.calendarContainer}>
                        <Calendar
                            value={instant ? value : innerValue}
                            onChange={handleDateChange}
                            formatShortWeekday={(_locale, date) => shortDays[date.getDay()]}
                            selectRange={range}
                            tileDisabled={enabledDates ? tileDisabled : undefined}
                            tileContent={markedDates ? tileContent : undefined}
                        />
                    </div>
                </div>
                {!instant && (
                    <div className={styles.datePickerFooter}>
                        <div className={styles.footerDateFlex}>
                            <ScoopText className={styles.footerDateDisplay}>
                                {getInnerValueDateString()}
                            </ScoopText>
                        </div>
                        <ScoopButton variant="ghost" size="sm" onClick={handleCancel}>
                            Cancel
                        </ScoopButton>
                        <ScoopButton variant="default" size="sm" onClick={handleApply}>
                            Apply
                        </ScoopButton>
                    </div>
                )}
            </ScoopMenu>
        </>
    )
}
